import styled from '@emotion/styled';
import Arrow from 'aquilla/icons/actions/Arrow';
import React, { useCallback, useContext, useMemo, useState } from 'react';
import { DoubtsAIContext } from '@context/DoubtsAIContext';
import useDoubtConfig from '@api/hooks/goal/custom-doubts/useDoubtConfig';
import Image from 'next/image';
import getAiSolution, {
  extractChatHistory
} from '@api/fetchers/doubts-and-solutions/ai-solution';
import useMyInfo from '@api/hooks/auth/useMyInfo';
import { useToggleable } from '@stores/toggleables';
import { LOGIN_DRAWER } from '@constants/toggleables';
import { getIsLoggedIn } from '@api/selectors/auth';
import {
  DOW2_DAILY_LIMIT_REACHED,
  DOW2_DOUBT_SUBMIT_FAILED,
  DOW2_DOUBT_SUBMIT_REQUESTED,
  DOW2_DOUBT_SUBMITTED,
  DOW2_SOLUTION_DISPLAYED
} from '@constants/segment';
import { segment } from '@actions/analytics';
import useGoalEventProps from '@hooks/useGoalEventProps';
import Cross from 'aquilla/icons/misc/Cross';
import {
  CHAT_IMAGE_INPUT_PLACEHOLDER,
  CHAT_INPUT_PLACEHOLDER,
  CHAT_MAX_INPUT_LENGTH
} from './constants';
import { ContentType, IChatMessage } from './types';
import useDoubtSubmission from './hooks/useDoubtSubmission';
import UnAIDoubtImageUpload from './DoubtsUpload/UnAIDoubtImageUpload';
import useDoubtSolution from './hooks/useDoubtSolution';

const Input = styled.textarea`
  border: none;
  background: transparent;
  font-size: 16px;
  resize: none;
  font-weight: 400;
  line-height: 32px;
  color: var(--color-text-primary);
  max-width: 100%;
  max-height: 200px;
  min-height: 32px;
  overflow-y: auto;
`;

const InputBoxFooter = styled.div`
  display: flex;
  justify-content: space-between;
`;

const VerticalDivider = styled.div`
  height: 16px;
`;

const SendButton = styled.button<{ disabled: boolean }>`
  border: none;
  background: transparent;
  cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
`;

const SendIcon = styled(Arrow)<{ disabled: boolean }>`
  background: var(--color-accent-blue);
  border-radius: 100%;
  height: 40px;
  width: 40px;
  cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
  color: white;
  opacity: ${({ disabled }) => (disabled ? 0.5 : 1.0)};
`;

const PreviewWrapper = styled.div`
  position: relative;
  width: fit-content;
`;

const PreviewImage = styled(Image)`
  object-fit: contain;
  box-shadow: 0px 2px 12px 0px #00000014;
  border-radius: 4px;
  border: 1px solid var(--color-divider, #e9eef2);
`;

const CrossWrapper = styled.div`
  position: absolute;
  right: -6px;
  top: -6px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  background-color: var(--color-base-fill);
  border-radius: 50%;

  svg {
    color: var(--color-base-light-0);
  }
`;

const InputBoxContainer = ({
  goalUID,
  loading,
  setPolling
}: {
  goalUID: string;
  loading?: boolean;
  setPolling: () => void;
}) => {
  const [inputValue, setInputValue] = useState('');
  const [signedUrl, setSignedUrl] = useState<{
    signed_url: string;
    public_url: string;
  }>();
  const isLoggedIn = useMyInfo(getIsLoggedIn);
  const { openElement: openLoginDrawer } = useToggleable(LOGIN_DRAWER);
  const [uploadedImageUrl, setUploadedImageUrl] = useState(null);

  const {
    addMessage,
    setFlowType,
    scrollToBottom,
    flowType,
    chats,
    submittedImageUrl,
    setImageAiResponse,
    setLimitReachedData,
    doubtNumber,
    setDoubtNumber,
    doubtsUID
  } = useContext(DoubtsAIContext);

  const { selectedSolutionUID } = useDoubtSolution({
    doubtUID: doubtsUID,
    goalUID
  });
  const routeGoalSegmentData = useGoalEventProps({
    goalUID
  });

  const {
    doubtConfigData: { isASDEnabled }
  } = useDoubtConfig(goalUID);
  const { handleDoubtSubmission, handleRemovePreview } = useDoubtSubmission({
    goalUID,
    isASDEnabled,
    public_url: signedUrl?.public_url,
    signed_url: signedUrl?.signed_url,
    setUploadedImageUrl,
    setPolling
  });

  const fetchResponse = useCallback(
    async (type: ContentType) => {
      const history = extractChatHistory(chats);
      const response = await getAiSolution({
        goalUID,
        history,
        attachments: [
          {
            content:
              type === ContentType.TEXT ? inputValue : signedUrl?.public_url,
            content_type: type
          }
        ]
      });
      return response;
    },
    [chats, goalUID, inputValue, signedUrl?.public_url]
  );

  const isTextEntered = useMemo(
    () => inputValue.length > 0,
    [inputValue.length]
  );

  const handleSubmit = (isText: boolean) => {
    if (!isLoggedIn) {
      openLoginDrawer();
      return;
    }
    scrollToBottom();
    setFlowType('loading');
    segment.track(DOW2_DOUBT_SUBMITTED, {
      doubt_type: 'Doubts AI',
      has_image: isText ? 'No' : 'Yes',
      doubt_id: isText ? 'NA' : doubtsUID,
      doubt_subject_selection: 'NA',
      // eslint-disable-next-line no-nested-ternary
      doubt_upload_status: isText ? 'NA' : signedUrl.public_url ? 'Yes' : 'No',
      ai_doubt_number_of_day: doubtNumber,
      text_doubt: isText ? inputValue : 'NA',
      text_doubt_length: isText ? inputValue.length : 'NA',
      doubt_submitted_to: 'Doubts AI',
      is_follow_up_question: chats.length >= 2 ? 'Yes' : 'No',
      question_number_current_thread: extractChatHistory(chats).length + 1,
      doubt_submit_flow: isText ? 'Text' : 'Image',
      ...routeGoalSegmentData
    });
    setDoubtNumber((prev) => prev + 1);
    if (isText) {
      const messageId = Date.now();
      fetchResponse(ContentType.TEXT).then((botMessage) => {
        if ('message' in botMessage) {
          setFlowType('error');
          segment.track(DOW2_DOUBT_SUBMIT_FAILED, {
            doubt_type: 'Doubts AI',
            has_image: 'No',
            doubt_id: doubtsUID,
            doubt_subject_selection: 'NA',
            doubt_upload_status: 'No',
            ai_doubt_number_of_day: doubtNumber,
            text_doubt: inputValue,
            text_doubt_length: inputValue.length ?? 0,
            doubt_submitted_to: 'Doubts AI',
            is_follow_up_question: chats.length >= 2 ? 'Yes' : 'No',
            question_number_current_thread: extractChatHistory(chats).length,
            failure_reason: botMessage.message,
            doubt_submit_flow: 'Text',
            failure_source: 'Doubts AI'
          });
          return;
        }
        // Sending events
        segment.track(DOW2_SOLUTION_DISPLAYED, {
          doubt_type: 'Doubts AI',
          doubt_id: doubtsUID ?? 'NA',
          solution_id: 'NA',
          text_solution: botMessage.solution?.[0].content,
          solution_uid: 'NA',
          text_doubt: inputValue,
          text_doubt_length: inputValue.length ?? 0,
          is_follow_up_question: chats.length >= 2 ? 'Yes' : 'No',
          question_number_current_thread: extractChatHistory(chats).length + 1,
          ai_doubt_number_of_day: doubtNumber,
          solution_type: 'AI Solution'
        });
        if (botMessage.daily_limit_exceeded) {
          setFlowType('limit');
          if (botMessage.sub_cta_details)
            setLimitReachedData({
              text: botMessage.sub_cta_details?.text,
              cta_text: botMessage.sub_cta_details?.cta_text
            });
          segment.track(DOW2_DAILY_LIMIT_REACHED, {
            doubt_type: 'Doubts AI',
            ...routeGoalSegmentData
          });
          return;
        }
        addMessage({
          id: messageId,
          text: botMessage?.solution?.[0].content,
          sender: 'bot',
          timestamp: new Date(),
          type: ContentType.TEXT
        });
        setFlowType('questionOptions');
      });

      segment.track(DOW2_DOUBT_SUBMIT_REQUESTED, {
        retake_image: 'No',
        submission_medium: 'Upload',
        doubt_type: 'Doubts AI',
        flow_type: 'Non_Demo',
        has_image: 'No',
        doubt_upload_status: 'N.A.',
        doubt_submit_flow: 'Text',
        text_doubt: inputValue,
        text_doubt_length: inputValue.length ?? 0,
        doubt_submitted_to: 'Doubts AI',
        ai_doubt_number_of_day: doubtNumber
      });

      const userMessage: IChatMessage = {
        id: messageId,
        type: ContentType.TEXT,
        sender: 'user',
        text: inputValue,
        timestamp: new Date()
      };
      addMessage(userMessage);
      setInputValue('');
    } else {
      if (chats.length === 0 && uploadedImageUrl) {
        chats.push({
          id: 123,
          image: uploadedImageUrl,
          sender: 'user',
          timestamp: new Date(),
          type: ContentType.IMAGE
        });
      }
      handleDoubtSubmission(() => {
        segment.track(DOW2_SOLUTION_DISPLAYED, {
          doubt_type: 'Doubts AI',
          doubt_id: doubtsUID ?? 'NA',
          solution_id: selectedSolutionUID ?? 'NA',
          text_solution: 'NA',
          solution_uid: 'NA',
          text_doubt: 'No',
          text_doubt_length: 'NA',
          is_follow_up_question: chats.length >= 2 ? 'Yes' : 'No',
          question_number_current_thread: extractChatHistory(chats).length + 1,
          ai_doubt_number_of_day: doubtNumber,
          solution_type: 'AAD'
        });
      });

      fetchResponse(ContentType.IMAGE).then((botMessage) => {
        if ('message' in botMessage) {
          setFlowType('error');
          segment.track(DOW2_DOUBT_SUBMIT_FAILED, {
            doubt_type: 'Doubts AI',
            has_image: 'Yes',
            doubt_id: doubtsUID,
            doubt_subject_selection: 'NA',
            doubt_upload_status: signedUrl.public_url ? 'Yes' : 'No',
            ai_doubt_number_of_day: doubtNumber,
            text_doubt: 'No',
            text_doubt_length: 'NA',
            doubt_submitted_to: 'Doubts AI',
            is_follow_up_question: chats.length >= 2 ? 'Yes' : 'No',
            question_number_current_thread: extractChatHistory(chats).length,
            doubt_submit_flow: 'Upload',
            failure_reason: 'Doubts AI'
          });
        } else {
          setImageAiResponse(botMessage);
          segment.track(DOW2_SOLUTION_DISPLAYED, {
            doubt_type: 'Doubts AI',
            doubt_id: doubtsUID,
            text_solution: botMessage.solution?.[0].content,
            solution_uid: 'NA',
            text_doubt: 'No',
            text_doubt_length: 'NA',
            is_follow_up_question: chats.length >= 2 ? 'Yes' : 'No',
            question_number_current_thread:
              extractChatHistory(chats).length + 1,
            ai_doubt_number_of_day: doubtNumber,
            solution_type: 'AI Solution'
          });
        }
      });

      segment.track(DOW2_DOUBT_SUBMIT_REQUESTED, {
        retake_image: 'No',
        submission_medium: 'Upload',
        doubt_type: 'Doubts AI',
        flow_type: 'Non_Demo',
        has_image: 'Yes',
        doubt_upload_status: signedUrl.public_url ? 'Success' : 'Failed',
        doubt_submit_flow: 'Upload',
        text_doubt: 'No',
        text_doubt_length: 0,
        doubt_submitted_to: 'Doubts AI',
        ai_doubt_number_of_day: doubtNumber
      });
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setInputValue(e.target.value);
    e.target.style.height = '32px';
    e.target.style.height = `${Math.min(e.target.scrollHeight, 200)}px`;
  };

  const isUploadDisabled = useMemo(
    () =>
      flowType === 'loading' ||
      chats.length !== 0 ||
      isTextEntered ||
      uploadedImageUrl ||
      submittedImageUrl,
    [chats.length, flowType, isTextEntered, submittedImageUrl, uploadedImageUrl]
  );

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault(); // Prevent new line
      handleSubmit(!!inputValue);
    }
  };

  return (
    <>
      {!loading ? (
        <>
          {uploadedImageUrl ? (
            <PreviewWrapper>
              <PreviewImage
                width={186}
                height={120}
                src={uploadedImageUrl}
                alt="doubt preview image"
              />
              <CrossWrapper>
                <Cross width={16} height={16} onClick={handleRemovePreview} />
              </CrossWrapper>
            </PreviewWrapper>
          ) : (
            <Input
              value={inputValue}
              maxLength={CHAT_MAX_INPUT_LENGTH}
              autoFocus
              rows={1}
              onChange={handleInputChange}
              onKeyDown={handleKeyDown}
              placeholder={
                isUploadDisabled
                  ? CHAT_INPUT_PLACEHOLDER
                  : CHAT_IMAGE_INPUT_PLACEHOLDER
              }
            />
          )}
          <VerticalDivider />
        </>
      ) : null}
      <InputBoxFooter>
        <UnAIDoubtImageUpload
          isUploadDisabled={isUploadDisabled}
          goalUID={goalUID}
          setUploadedImageUrl={setUploadedImageUrl}
          setSignedUrl={setSignedUrl}
        />
        <SendButton
          disabled={!(isTextEntered || uploadedImageUrl)}
          onClick={() => handleSubmit(isTextEntered)}
        >
          <SendIcon disabled={!(isTextEntered || uploadedImageUrl)} />
        </SendButton>
      </InputBoxFooter>
    </>
  );
};

export default InputBoxContainer;
